import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Instagram, ExternalLink } from 'lucide-react';
import Lottie from 'lottie-react';
import { fetchAlly } from '../services/alliesService';
import { byDisplayOrder } from '../utils/sortByDisplayOrder';
import AllyProductCard from '../components/AllyProductCard';
import loadingAnimation from '../assets/loading.json';
import usePageMeta from '../hooks/usePageMeta';
import './AllyPage.css';

const AllyPage = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const [ally, setAlly] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productsWarning, setProductsWarning] = useState(null);

  usePageMeta({
    title: ally ? `${ally.name} — Bluppimart` : 'Aliado — Bluppimart',
    description: ally?.description?.slice(0, 200) || undefined,
    image: ally?.profile_image_url || undefined,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchAlly(identifier);
        if (cancelled) return;
        setAlly(data.ally);
        setProducts(data.products || []);
        if (data.warning) setProductsWarning(data.warning);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Error al cargar el aliado.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (identifier) load();
    return () => { cancelled = true; };
  }, [identifier]);

  const sortedProducts = useMemo(() =>
    [...products].sort(byDisplayOrder),
    [products]
  );

  if (loading) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <Lottie
          animationData={loadingAnimation}
          loop
          style={{ width: 200, height: 200, margin: '0 auto' }}
        />
      </div>
    );
  }

  if (error || !ally) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <p style={{ color: '#dc3545', marginBottom: '1rem', fontSize: '1.2rem' }}>
          {error || 'Aliado no encontrado.'}
        </p>
        <button onClick={() => navigate('/allies')} className="btn btn-primary">
          Ver todos los aliados
        </button>
      </div>
    );
  }

  return (
    <article className="ally-page">
      <div className="container ally-card">
        {/* Profile header */}
        <div className="ally-header-inner">
          {ally.profile_image_url && (
            <figure className="ally-header-figure">
              <img
                src={ally.profile_image_url}
                alt={ally.name}
                ref={(el) => {
                  if (el?.complete) {
                    el.classList.add('is-loaded');
                    el.parentElement?.classList.add('is-loaded');
                  }
                }}
                onLoad={(e) => {
                  e.currentTarget.classList.add('is-loaded');
                  e.currentTarget.parentElement.classList.add('is-loaded');
                }}
                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
              />
            </figure>
          )}
          <div className="ally-header-body">
            <h1 className="ally-header-name">{ally.name}</h1>
            {ally.description && (
              <p className="ally-header-description">{ally.description}</p>
            )}
            <div className="ally-header-links">
              {ally.instagram_handle && (
                <a
                  className="ally-header-link"
                  href={`https://www.instagram.com/${ally.instagram_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Instagram de ${ally.name}: ${ally.instagram_handle}`}
                >
                  <Instagram size={16} aria-hidden="true" />
                  {ally.instagram_handle}
                </a>
              )}
              {ally.website_url && (
                <a
                  className="ally-header-link"
                  href={ally.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={16} aria-hidden="true" />
                  Sitio web
                </a>
              )}
            </div>
          </div>
        </div>

        {productsWarning && (
          <p style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '1.5rem' }}>{productsWarning}</p>
        )}

        {/* Product catalog */}
        {(ally.catalog_title || ally.catalog_description || sortedProducts.length > 0) && (
          <div className="ally-catalog">
            {ally.catalog_title && (
              <h2 className="ally-catalog-title">{ally.catalog_title}</h2>
            )}
            {ally.catalog_description && (
              <p className="ally-catalog-description">{ally.catalog_description}</p>
            )}
            {sortedProducts.length > 0 && (
              <div className="products-grid">
                {sortedProducts.map((product) => (
                  <AllyProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default AllyPage;
